// src/pages/dashboard/Sales.jsx
import { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import ConfirmationNumberRoundedIcon from "@mui/icons-material/ConfirmationNumberRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import AccountBalanceRoundedIcon from "@mui/icons-material/AccountBalance";
import CreditCardRoundedIcon from "@mui/icons-material/CreditCard";
import EventRoundedIcon from "@mui/icons-material/EventRounded";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";

import { get, onValue, off, ref } from "firebase/database";
import { rtdb } from "../../services/firebase/rtdb";
import { useAuthContext } from "../../contexts/AuthContext";

// ─── helpers ──────────────────────────────────────────────────────────────────

function fmt(n) {
  return Number(n || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function fmtDate(ms) {
  if (!ms) return "—";
  return new Date(Number(ms)).toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function fmtShortDate(ms) {
  if (!ms) return "—";
  return new Date(Number(ms)).toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "short",
  });
}

const PERIODS = [
  { label: "7 dias", value: 7 },
  { label: "30 dias", value: 30 },
  { label: "90 dias", value: 90 },
  { label: "Tudo", value: 0 },
];

const glass = {
  p: { xs: 2, md: 2.5 },
  borderRadius: "20px",
  background: "rgba(255,255,255,0.06)",
  border: "1px solid rgba(255,255,255,0.12)",
  backdropFilter: "blur(18px)",
};

// ─── mini barra proporcional ──────────────────────────────────────────────────

function MiniBar({ value, max, color = "#69f0ae" }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <Box sx={{ flex: 1, height: 6, borderRadius: 4, background: "rgba(255,255,255,0.08)", overflow: "hidden" }}>
      <Box sx={{ width: `${pct}%`, height: "100%", background: color, borderRadius: 4, transition: "width .5s ease" }} />
    </Box>
  );
}

// ─── componente principal ─────────────────────────────────────────────────────

export default function Sales() {
  const { user } = useAuthContext();
  const organizerId = user?.uid;

  const [period, setPeriod] = useState(30);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [myEvents, setMyEvents] = useState([]);   // eventos do organizador
  const [orders, setOrders] = useState([]);        // pedidos filtrados por evento

  // ── carrega eventos do organizador ────────────────────────────────────────
  useEffect(() => {
    if (!organizerId) { setLoadingEvents(false); return; }

    const evRef = ref(rtdb, "events");
    const handler = (snap) => {
      const val = snap.val() || {};
      const list = Object.entries(val)
        .map(([id, d]) => ({ id, ...d }))
        .filter((e) => e.organizerId === organizerId);
      setMyEvents(list);
      setLoadingEvents(false);
    };
    onValue(evRef, handler, () => { setMyEvents([]); setLoadingEvents(false); });
    return () => off(evRef, "value", handler);
  }, [organizerId]);

  // ── carrega pedidos de todos os eventos do organizador ────────────────────
  useEffect(() => {
    if (!organizerId || myEvents.length === 0) {
      setOrders([]);
      setLoadingOrders(false);
      return;
    }

    const eventIds = new Set(myEvents.map((e) => e.id));

    const fetchOrders = async () => {
      setLoadingOrders(true);
      try {
        const snap = await get(ref(rtdb, "orders"));
        if (!snap.exists()) { setOrders([]); return; }

        const all = Object.values(snap.val() || {});
        const filtered = all.filter((o) => eventIds.has(o.eventId));
        setOrders(filtered.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0)));
      } catch (err) {
        console.error(err);
        setOrders([]);
      } finally {
        setLoadingOrders(false);
      }
    };

    fetchOrders();
  }, [myEvents, organizerId]);

  const loading = loadingEvents || loadingOrders;

  // ── filtra por período ────────────────────────────────────────────────────
  const periodMs = period > 0 ? Date.now() - period * 86_400_000 : 0;

  const filtered = useMemo(
    () => orders.filter((o) => !periodMs || (o.createdAt || 0) >= periodMs),
    [orders, periodMs],
  );

  // ── métricas globais ──────────────────────────────────────────────────────
  const kpis = useMemo(() => {
    const revenue = filtered.reduce((acc, o) => acc + (o.summary?.total || 0), 0);
    const tickets = filtered.reduce((acc, o) => acc + (o.ticketCount || 0), 0);
    const total = filtered.length;
    const avg = tickets > 0 ? revenue / tickets : 0;
    return { revenue, tickets, total, avg };
  }, [filtered]);

  // ── receita por evento ────────────────────────────────────────────────────
  const byEvent = useMemo(() => {
    const map = {};
    for (const o of filtered) {
      if (!map[o.eventId]) {
        map[o.eventId] = {
          eventId: o.eventId,
          title: o.eventTitle || "—",
          revenue: 0,
          tickets: 0,
          orders: 0,
        };
      }
      map[o.eventId].revenue += o.summary?.total || 0;
      map[o.eventId].tickets += o.ticketCount || 0;
      map[o.eventId].orders += 1;
    }
    return Object.values(map).sort((a, b) => b.revenue - a.revenue);
  }, [filtered]);

  const maxEventRevenue = byEvent[0]?.revenue || 0;

  // ── por método de pagamento ───────────────────────────────────────────────
  const byPayment = useMemo(() => {
    const map = { pix: { count: 0, revenue: 0 }, credit: { count: 0, revenue: 0 }, other: { count: 0, revenue: 0 } };
    for (const o of filtered) {
      const key = o.paymentMethod === "pix" ? "pix" : o.paymentMethod === "credit" ? "credit" : "other";
      map[key].count += 1;
      map[key].revenue += o.summary?.total || 0;
    }
    return map;
  }, [filtered]);

  // ── ingressos por tipo (cross-event) ─────────────────────────────────────
  const byTicketType = useMemo(() => {
    const map = {};
    for (const o of filtered) {
      for (const item of o.items || []) {
        const key = item.name || "Desconhecido";
        if (!map[key]) map[key] = { name: key, qty: 0, revenue: 0 };
        map[key].qty += item.quantity || 0;
        map[key].revenue += item.lineTotal || 0;
      }
    }
    return Object.values(map).sort((a, b) => b.qty - a.qty);
  }, [filtered]);

  const maxQty = byTicketType[0]?.qty || 0;

  // ── pedidos recentes ──────────────────────────────────────────────────────
  const recentOrders = filtered.slice(0, 15);

  // ─────────────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1600, margin: "0 auto" }}>
      {/* Header */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={1.5}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="overline" sx={{ color: "rgba(255,255,255,0.6)" }}>
            VENDAS & RECEITA
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 900 }}>
            Painel de Vendas
          </Typography>
        </Box>

        <FormControl size="small" sx={{ minWidth: 140 }}>
          <Select
            value={period}
            onChange={(e) => setPeriod(Number(e.target.value))}
            sx={{
              borderRadius: "12px",
              background: "rgba(255,255,255,0.06)",
              color: "white",
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.16)" },
              "& .MuiSvgIcon-root": { color: "white" },
            }}
          >
            {PERIODS.map((p) => (
              <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {loading ? (
        <Box sx={{ py: 12, display: "grid", placeItems: "center" }}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={3}>
          {/* ── KPIs ── */}
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" }, gap: 2 }}>
            <KpiCard
              label="Receita bruta"
              value={fmt(kpis.revenue)}
              icon={<TrendingUpRoundedIcon />}
              color="#40c4ff"
              bg="rgba(0,180,255,0.15)"
            />
            <KpiCard
              label="Ingressos vendidos"
              value={kpis.tickets}
              icon={<ConfirmationNumberRoundedIcon />}
              color="#69f0ae"
              bg="rgba(0,255,180,0.15)"
            />
            <KpiCard
              label="Pedidos"
              value={kpis.total}
              icon={<ReceiptLongRoundedIcon />}
              color="#ffd54f"
              bg="rgba(255,200,0,0.15)"
            />
            <KpiCard
              label="Ticket médio"
              value={fmt(kpis.avg)}
              icon={<TrendingUpRoundedIcon />}
              color="#ce93d8"
              bg="rgba(206,147,216,0.15)"
            />
          </Box>

          {/* ── receita por evento + método de pagamento ── */}
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2, alignItems: "start" }}>
            {/* por evento */}
            <Paper sx={glass}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <EventRoundedIcon sx={{ opacity: 0.7, fontSize: 20 }} />
                <Typography sx={{ fontWeight: 800, fontSize: 15 }}>Receita por evento</Typography>
              </Stack>

              {byEvent.length === 0 ? (
                <EmptyMsg text="Nenhuma venda no período selecionado." />
              ) : (
                <Stack spacing={1.8}>
                  {byEvent.map((ev) => (
                    <Box key={ev.eventId}>
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 0.6, gap: 1, flexWrap: "wrap" }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {ev.title}
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
                          <Typography sx={{ fontSize: 12, opacity: 0.6, display: { xs: "none", sm: "block" } }}>{ev.tickets} ingressos</Typography>
                          <Typography sx={{ fontSize: 13, fontWeight: 900, color: "#69f0ae" }}>
                            {fmt(ev.revenue)}
                          </Typography>
                        </Stack>
                      </Stack>
                      <MiniBar value={ev.revenue} max={maxEventRevenue} />
                    </Box>
                  ))}
                </Stack>
              )}
            </Paper>

            {/* método de pagamento */}
            <Paper sx={glass}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <AccountBalanceRoundedIcon sx={{ opacity: 0.7, fontSize: 20 }} />
                <Typography sx={{ fontWeight: 800, fontSize: 15 }}>Forma de pagamento</Typography>
              </Stack>

              <Stack spacing={2}>
                <PaymentRow
                  icon={<AccountBalanceRoundedIcon sx={{ fontSize: 18 }} />}
                  label="Pix"
                  count={byPayment.pix.count}
                  revenue={byPayment.pix.revenue}
                  total={kpis.revenue}
                  color="#69f0ae"
                />
                <PaymentRow
                  icon={<CreditCardRoundedIcon sx={{ fontSize: 18 }} />}
                  label="Cartão de crédito"
                  count={byPayment.credit.count}
                  revenue={byPayment.credit.revenue}
                  total={kpis.revenue}
                  color="#80d8ff"
                />
                {byPayment.other.count > 0 && (
                  <PaymentRow
                    icon={<ReceiptLongRoundedIcon sx={{ fontSize: 18 }} />}
                    label="Outros"
                    count={byPayment.other.count}
                    revenue={byPayment.other.revenue}
                    total={kpis.revenue}
                    color="#ffd54f"
                  />
                )}
              </Stack>

              <Divider sx={{ my: 2, borderColor: "rgba(255,255,255,0.1)" }} />

              <Stack direction="row" justifyContent="space-between">
                <Typography sx={{ fontSize: 13, opacity: 0.65 }}>Total no período</Typography>
                <Typography sx={{ fontSize: 15, fontWeight: 900 }}>{fmt(kpis.revenue)}</Typography>
              </Stack>
            </Paper>
          </Box>

          {/* ── pedidos recentes ── */}
          <Paper sx={glass}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <ReceiptLongRoundedIcon sx={{ opacity: 0.7, fontSize: 20 }} />
              <Typography sx={{ fontWeight: 800, fontSize: 15 }}>
                Pedidos recentes
                {filtered.length > 0 && (
                  <Typography component="span" sx={{ ml: 1, fontSize: 12, opacity: 0.55, fontWeight: 400 }}>
                    ({filtered.length} no período)
                  </Typography>
                )}
              </Typography>
            </Stack>

            {recentOrders.length === 0 ? (
              <EmptyMsg text="Nenhum pedido no período selecionado." />
            ) : (
              <>
                {/* Mobile: cards empilhados */}
                <Stack spacing={1.2} sx={{ display: { xs: "flex", sm: "none" } }}>
                  {recentOrders.map((o) => (
                    <Box
                      key={o.orderId}
                      sx={{
                        p: 1.5,
                        borderRadius: "12px",
                        background: "rgba(255,255,255,0.04)",
                        border: "1px solid rgba(255,255,255,0.08)",
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography sx={{ fontWeight: 700, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {o.buyer?.name || "—"}
                          </Typography>
                          <Typography sx={{ fontSize: 11, opacity: 0.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {o.eventTitle || "—"}
                          </Typography>
                        </Box>
                        <Typography sx={{ fontWeight: 900, fontSize: 14, color: "#69f0ae", ml: 1, flexShrink: 0 }}>
                          {fmt(o.summary?.total)}
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={0.8} sx={{ mt: 1, flexWrap: "wrap", gap: 0.5 }}>
                        <Chip
                          size="small"
                          label={`${o.ticketCount} ingresso${o.ticketCount !== 1 ? "s" : ""}`}
                          sx={{ height: 20, fontSize: 10, fontWeight: 800, background: "rgba(105,240,174,0.12)", color: "#69f0ae", border: "1px solid rgba(105,240,174,0.25)" }}
                        />
                        <Chip
                          size="small"
                          icon={o.paymentMethod === "pix" ? <AccountBalanceRoundedIcon sx={{ fontSize: "12px !important" }} /> : <CreditCardRoundedIcon sx={{ fontSize: "12px !important" }} />}
                          label={o.paymentMethod === "pix" ? "Pix" : "Cartão"}
                          sx={{ height: 20, fontSize: 10, fontWeight: 700, background: "rgba(255,255,255,0.07)", color: "white" }}
                        />
                        <Typography sx={{ fontSize: 10, opacity: 0.45, alignSelf: "center" }}>
                          {fmtShortDate(o.createdAt)}
                        </Typography>
                      </Stack>
                    </Box>
                  ))}
                </Stack>

                {/* Desktop: tabela */}
                <Box sx={{ overflowX: "auto", display: { xs: "none", sm: "block" } }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {["Comprador", "Evento", "Ingressos", "Pagamento", "Total", "Data"].map((h) => (
                          <TableCell key={h} sx={{ color: "rgba(255,255,255,0.5)", fontWeight: 700, borderBottom: "1px solid rgba(255,255,255,0.08)", whiteSpace: "nowrap" }}>
                            {h}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {recentOrders.map((o) => (
                        <TableRow key={o.orderId} sx={{ "&:last-child td": { border: 0 }, "&:hover": { background: "rgba(255,255,255,0.03)" } }}>
                          <TableCell sx={{ color: "white", border: "none", whiteSpace: "nowrap" }}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Avatar sx={{ width: 28, height: 28, fontSize: 12, background: "rgba(255,255,255,0.12)" }}>
                                <PersonOutlineRoundedIcon sx={{ fontSize: 16 }} />
                              </Avatar>
                              <Box>
                                <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{o.buyer?.name || "—"}</Typography>
                                <Typography sx={{ fontSize: 11, opacity: 0.55 }}>{o.buyer?.email || ""}</Typography>
                              </Box>
                            </Stack>
                          </TableCell>
                          <TableCell sx={{ color: "rgba(255,255,255,0.85)", border: "none", maxWidth: 180 }}>
                            <Typography sx={{ fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {o.eventTitle || "—"}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ color: "white", border: "none", textAlign: "center" }}>
                            <Chip
                              size="small"
                              label={o.ticketCount}
                              sx={{ height: 22, fontWeight: 900, background: "rgba(105,240,174,0.12)", color: "#69f0ae", border: "1px solid rgba(105,240,174,0.3)" }}
                            />
                          </TableCell>
                          <TableCell sx={{ border: "none" }}>
                            <Chip
                              size="small"
                              icon={o.paymentMethod === "pix" ? <AccountBalanceRoundedIcon sx={{ fontSize: "14px !important" }} /> : <CreditCardRoundedIcon sx={{ fontSize: "14px !important" }} />}
                              label={o.paymentMethod === "pix" ? "Pix" : "Cartão"}
                              sx={{ height: 22, fontWeight: 800, background: "rgba(255,255,255,0.08)", color: "white" }}
                            />
                          </TableCell>
                          <TableCell sx={{ color: "#69f0ae", fontWeight: 900, border: "none", whiteSpace: "nowrap" }}>
                            {fmt(o.summary?.total)}
                          </TableCell>
                          <TableCell sx={{ color: "rgba(255,255,255,0.55)", border: "none", fontSize: 12, whiteSpace: "nowrap" }}>
                            {fmtDate(o.createdAt)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </>
            )}
          </Paper>

          {/* ── ingressos por tipo ── */}
          {byTicketType.length > 0 && (
            <Paper sx={glass}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <ConfirmationNumberRoundedIcon sx={{ opacity: 0.7, fontSize: 20 }} />
                <Typography sx={{ fontWeight: 800, fontSize: 15 }}>Vendas por tipo de ingresso</Typography>
              </Stack>

              {/* Mobile: cards */}
              <Stack spacing={1.2} sx={{ display: { xs: "flex", sm: "none" } }}>
                {byTicketType.map((tt) => (
                  <Box
                    key={tt.name}
                    sx={{
                      p: 1.5,
                      borderRadius: "12px",
                      background: "rgba(255,255,255,0.04)",
                      border: "1px solid rgba(255,255,255,0.08)",
                    }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.8 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: 13 }}>{tt.name}</Typography>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Typography sx={{ fontSize: 12, opacity: 0.6 }}>{tt.qty} und.</Typography>
                        <Typography sx={{ fontWeight: 900, fontSize: 13, color: "#69f0ae" }}>{fmt(tt.revenue)}</Typography>
                      </Stack>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <MiniBar value={tt.qty} max={maxQty} color="#ce93d8" />
                      <Typography sx={{ fontSize: 11, opacity: 0.6, flexShrink: 0 }}>
                        {maxQty > 0 ? `${Math.round((tt.qty / maxQty) * 100)}%` : "0%"}
                      </Typography>
                    </Stack>
                  </Box>
                ))}
              </Stack>

              {/* Desktop: tabela */}
              <Box sx={{ overflowX: "auto", display: { xs: "none", sm: "block" } }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {["Tipo", "Qtd. vendida", "Participação", "Receita"].map((h) => (
                        <TableCell key={h} sx={{ color: "rgba(255,255,255,0.5)", fontWeight: 700, borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {byTicketType.map((tt) => (
                      <TableRow key={tt.name} sx={{ "&:last-child td": { border: 0 } }}>
                        <TableCell sx={{ color: "white", fontWeight: 700, border: "none" }}>{tt.name}</TableCell>
                        <TableCell sx={{ color: "white", border: "none" }}>{tt.qty}</TableCell>
                        <TableCell sx={{ border: "none", minWidth: 140 }}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <MiniBar value={tt.qty} max={maxQty} color="#ce93d8" />
                            <Typography sx={{ fontSize: 12, opacity: 0.7, flexShrink: 0 }}>
                              {maxQty > 0 ? `${Math.round((tt.qty / maxQty) * 100)}%` : "0%"}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ color: "#69f0ae", fontWeight: 900, border: "none" }}>{fmt(tt.revenue)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Paper>
          )}
        </Stack>
      )}
    </Box>
  );
}

// ─── sub-componentes ──────────────────────────────────────────────────────────

function KpiCard({ label, value, icon, color, bg }) {
  return (
    <Paper sx={{ ...glass, display: "flex", flexDirection: "column" }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)", fontWeight: 700, letterSpacing: "0.06em" }}>
            {label.toUpperCase()}
          </Typography>
          <Typography sx={{ fontWeight: 900, fontSize: { xs: 18, sm: 22 }, mt: 0.3 }}>
            {value}
          </Typography>
        </Box>
        <Avatar sx={{ bgcolor: bg, color, width: 40, height: 40 }}>{icon}</Avatar>
      </Stack>
    </Paper>
  );
}

function PaymentRow({ icon, label, count, revenue, total, color }) {
  const pct = total > 0 ? Math.round((revenue / total) * 100) : 0;
  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.8, flexWrap: "wrap", gap: 0.5 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box sx={{ color, opacity: 0.85 }}>{icon}</Box>
          <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{label}</Typography>
          <Chip size="small" label={`${count} pedido${count !== 1 ? "s" : ""}`} sx={{ height: 20, fontSize: 11, background: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.7)" }} />
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography sx={{ fontSize: 12, opacity: 0.6 }}>{pct}%</Typography>
          <Typography sx={{ fontSize: 13, fontWeight: 900, color }}>{fmt(revenue)}</Typography>
        </Stack>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{
          height: 5,
          borderRadius: 4,
          bgcolor: "rgba(255,255,255,0.08)",
          "& .MuiLinearProgress-bar": { bgcolor: color, borderRadius: 4 },
        }}
      />
    </Box>
  );
}

function EmptyMsg({ text }) {
  return (
    <Typography sx={{ opacity: 0.5, fontSize: 13, textAlign: "center", py: 2 }}>
      {text}
    </Typography>
  );
}
